import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Check, Navigation, Share2, ThumbsDown, ThumbsUp, Users } from 'lucide-react';
import groupVoteService from '../services/groupVoteService';
import { tallyVotes } from '../utils/voteTally';
import { formatPriceRange } from '../utils/priceRange';

const readName = () => { try { return localStorage.getItem('chi_na_jian_voter_name') || ''; } catch { return ''; } };

const GroupVote = ({ voteId, chinese = true, onNavigate, onExit }) => {
  const [vote, setVote] = useState(undefined);
  const [ballots, setBallots] = useState([]);
  const [failed, setFailed] = useState(false);
  const [name, setName] = useState(readName);
  const [picks, setPicks] = useState({});
  const [saveError, setSaveError] = useState('');
  const [status, setStatus] = useState('');
  const voterId = useMemo(() => groupVoteService.getVoterId(), []);
  const loadedOwnBallot = useRef(false);

  useEffect(() => {
    loadedOwnBallot.current = false;
    return groupVoteService.subscribe(voteId, {
      onVote: setVote,
      onBallots: next => {
        setBallots(next);
        const own = next.find(ballot => ballot.id === voterId);
        if (own && !loadedOwnBallot.current) {
          loadedOwnBallot.current = true;
          setPicks(own.picks && typeof own.picks === 'object' ? own.picks : {});
          if (typeof own.name === 'string' && own.name) setName(own.name);
        }
      },
      onError: () => setFailed(true)
    });
  }, [voteId, voterId]);

  const places = vote?.places || [];
  // Show my latest choice immediately, before Firestore echoes it back.
  const merged = useMemo(() => [...ballots.filter(ballot => ballot.id !== voterId), { id: voterId, name, picks }], [ballots, name, picks, voterId]);
  const tally = useMemo(() => tallyVotes(places, merged), [merged, places]);

  const save = async (nextPicks, nextName = name) => {
    setSaveError('');
    try { await groupVoteService.castBallot(voteId, voterId, nextName, nextPicks); }
    catch { setSaveError(chinese ? '投票沒有送出，請檢查網路後再點一次。' : 'Your vote was not saved. Check your connection and try again.'); }
  };

  const choose = (placeId, pick) => {
    const next = { ...picks };
    if (next[placeId] === pick) delete next[placeId]; else next[placeId] = pick;
    setPicks(next);
    save(next);
  };

  const commitName = () => {
    try { localStorage.setItem('chi_na_jian_voter_name', name.trim()); } catch {}
    if (Object.keys(picks).length) save(picks, name);
  };

  const share = async () => {
    const url = groupVoteService.shareUrl(voteId);
    try {
      if (navigator.share) await navigator.share({ title: chinese ? '今天吃哪間？' : 'Where should we eat?', text: chinese ? '幫我投一下，選好就出發！' : 'Vote so we can go!', url });
      else { await navigator.clipboard.writeText(url); setStatus(chinese ? '投票連結已複製' : 'Vote link copied'); }
    } catch (error) {
      if (error.name !== 'AbortError') setStatus(chinese ? '無法分享，請手動複製網址。' : 'Could not share. Copy the address manually.');
    }
  };

  if (failed || vote === null || vote?.expired) {
    return <section className="group-vote" aria-labelledby="vote-title"><div className="vote-empty"><Users aria-hidden="true" /><h1 id="vote-title">{vote?.expired ? (chinese ? '這個投票已結束' : 'This vote has ended') : (chinese ? '找不到這個投票' : 'Vote not found')}</h1><p>{chinese ? '投票連結 24 小時後失效。可以回到探索重新找、再開一個投票。' : 'Vote links expire after 24 hours. Search again to start a new one.'}</p><button className="primary-button" onClick={onExit}>{chinese ? '回到探索' : 'Back to explore'}</button></div></section>;
  }
  if (vote === undefined) return <section className="group-vote"><div className="loading-state" role="status"><span className="spinner" />{chinese ? '載入投票中…' : 'Loading vote…'}</div></section>;

  const leader = tally.leader;
  return (
    <section className="group-vote" aria-labelledby="vote-title">
      <div className="vote-heading">
        <button className="text-button" onClick={onExit}><ArrowLeft aria-hidden="true" />{chinese ? '回到探索' : 'Back to explore'}</button>
        <p className="eyebrow"><Users aria-hidden="true" />{chinese ? `${tally.voters} 人已投票・24 小時內有效` : `${tally.voters} voted · open for 24 hours`}</p>
        <h1 id="vote-title">{vote.title || (chinese ? '今天吃哪間？' : 'Where should we eat?')}</h1>
        <p>{chinese ? '每間店點「想去」或「不要」，結果即時更新。不用登入，也不會分享你的位置。' : 'Tap Yes or No on each place. Results update live. No sign-in, and no locations are shared.'}</p>
        <button className="secondary-button" onClick={share}><Share2 aria-hidden="true" />{chinese ? '邀請朋友投票' : 'Invite friends'}</button>
      </div>

      <div className={`vote-result ${tally.agreed ? 'is-agreed' : ''}`} role="status" aria-live="polite">
        {leader ? <>
          <span>{tally.agreed ? <Check aria-hidden="true" /> : <ThumbsUp aria-hidden="true" />}</span>
          <div><small>{tally.agreed ? (chinese ? '大家都同意' : 'Everyone agrees') : (chinese ? '目前領先' : 'Leading now')}</small><strong>{leader.place.name}</strong><em>{chinese ? `${leader.yes} 人想去${leader.no ? `・${leader.no} 人不要` : ''}` : `${leader.yes} yes${leader.no ? ` · ${leader.no} no` : ''}`}</em></div>
          {leader.place.geometry && <button className="primary-button" onClick={() => onNavigate(leader.place)}><Navigation aria-hidden="true" />{chinese ? '出發' : 'Go'}</button>}
        </> : <p>{chinese ? '還沒有人選「想去」。先投下你的一票吧！' : 'No one has said yes yet. Cast the first vote!'}</p>}
      </div>

      <label className="vote-name"><span>{chinese ? '你的暱稱（選填）' : 'Your name (optional)'}</span><input value={name} maxLength={20} onChange={event => setName(event.target.value)} onBlur={commitName} placeholder={chinese ? '讓朋友知道是誰投的' : 'So friends know who voted'} /></label>
      {saveError && <div className="error-banner" role="alert"><span>{saveError}</span></div>}
      {status && <p className="vote-status" role="status">{status}</p>}

      <ol className="vote-list">
        {tally.rows.map(({ place, yes, no, yesNames }) => {
          const pick = picks[place.place_id];
          const price = formatPriceRange(place.price_level, chinese);
          return (
            <li key={place.place_id} className={`vote-card ${leader?.place.place_id === place.place_id ? 'is-leading' : ''}`}>
              <div className="vote-card-copy">
                <h2>{place.name}</h2>
                <p>{[place.cuisineLabel, place.walkMinutes != null && (chinese ? `步行約 ${place.walkMinutes} 分鐘` : `${place.walkMinutes} min walk`), place.rating != null && `★ ${place.rating.toFixed(1)}（${place.user_ratings_total}）`, price].filter(Boolean).join(' · ')}</p>
                <small>{yes ? (chinese ? `想去：${yesNames.map(item => item || '朋友').join('、')}` : `Yes: ${yesNames.map(item => item || 'Friend').join(', ')}`) : (chinese ? '還沒人想去' : 'No yes votes yet')}{no ? (chinese ? `・${no} 人不要` : ` · ${no} no`) : ''}</small>
              </div>
              <div className="vote-buttons" role="group" aria-label={chinese ? `${place.name} 投票` : `Vote for ${place.name}`}>
                <button className={pick === 'yes' ? 'active yes' : ''} aria-pressed={pick === 'yes'} onClick={() => choose(place.place_id, 'yes')}><ThumbsUp aria-hidden="true" />{chinese ? '想去' : 'Yes'}</button>
                <button className={pick === 'no' ? 'active no' : ''} aria-pressed={pick === 'no'} onClick={() => choose(place.place_id, 'no')}><ThumbsDown aria-hidden="true" />{chinese ? '不要' : 'No'}</button>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
};

export default GroupVote;
